import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, RefreshCcw, TrendingUp } from 'lucide-react';
import {
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  Rozet,
  TarihAraligiSecici,
  tarihIso,
  type TarihAraligi,
} from '@emanetx/ui';
import { gelirRaporuGetir } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

const PARA_SEMBOL: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

function son30Gun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaGelir() {
  const [aralik, setAralik] = useState<TarihAraligi>(son30Gun);

  const sorgu =
    aralik.baslangic && aralik.bitis
      ? { baslangic: tarihIso(aralik.baslangic)!, bitis: tarihIso(aralik.bitis)! }
      : null;

  const rapor = useQuery({
    queryKey: ['raporlar', 'gelir', sorgu],
    queryFn: () => gelirRaporuGetir(istemci, sorgu!),
    enabled: !!sorgu,
  });

  const maxGunluk = Math.max(...(rapor.data?.gunluk.map((g) => Number(g.tutar)) ?? [0]), 1);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Gelir Raporu</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Başarılı ödemelerin para birimi, sağlayıcı ve günlük dağılımı.
          </p>
        </div>
        <TarihAraligiSecici deger={aralik} onDegisim={setAralik} />
      </header>

      {rapor.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Iskelet className="h-32" />
          <Iskelet className="h-32" />
          <Iskelet className="h-32" />
        </div>
      ) : !rapor.data ? null : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rapor.data.toplam.length === 0 ? (
              <Kart className="lg:col-span-3">
                <KartIcerik className="text-fume-500 py-8 text-center text-sm">
                  Bu aralıkta başarılı ödeme yok.
                </KartIcerik>
              </Kart>
            ) : (
              rapor.data.toplam.map((t) => (
                <Kart key={t.paraBirimi}>
                  <KartBaslik>
                    <div className="flex items-center justify-between">
                      <h3 className="text-fume-500 text-xs font-medium uppercase tracking-wider">
                        Toplam Gelir
                      </h3>
                      <Rozet ton="yesil">{t.paraBirimi}</Rozet>
                    </div>
                  </KartBaslik>
                  <KartIcerik>
                    <div className="font-mono text-3xl font-bold">
                      {PARA_SEMBOL[t.paraBirimi]}
                      {Number(t.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                    <p className="text-fume-400 mt-1 text-xs">{t.sayi} başarılı ödeme</p>
                  </KartIcerik>
                </Kart>
              ))
            )}
          </div>

          {rapor.data.iadeToplam.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rapor.data.iadeToplam.map((i) => (
                <Kart key={i.paraBirimi}>
                  <KartBaslik>
                    <div className="flex items-center justify-between">
                      <h3 className="text-fume-500 text-xs font-medium uppercase tracking-wider">
                        İade Toplam
                      </h3>
                      <Rozet ton="turuncu">{i.paraBirimi}</Rozet>
                    </div>
                  </KartBaslik>
                  <KartIcerik>
                    <div className="font-mono text-2xl font-bold text-red-600 dark:text-red-400">
                      −{PARA_SEMBOL[i.paraBirimi]}
                      {Number(i.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                    <p className="text-fume-400 mt-1 text-xs">{i.sayi} iade</p>
                  </KartIcerik>
                </Kart>
              ))}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <Kart className="lg:col-span-2">
              <KartBaslik>
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <TrendingUp size={16} className="text-marka-500" /> Günlük Trend
                </h3>
              </KartBaslik>
              <KartIcerik>
                {rapor.data.gunluk.length === 0 ? (
                  <p className="text-fume-500 py-6 text-center text-sm">Gün verisi yok.</p>
                ) : (
                  <div className="space-y-1.5">
                    {rapor.data.gunluk.map((g) => {
                      const w = (Number(g.tutar) / maxGunluk) * 100;
                      return (
                        <div key={g.gun} className="flex items-center gap-3">
                          <span className="text-fume-500 w-16 shrink-0 font-mono text-xs">
                            {new Date(g.gun).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                          <div className="bg-fume-100 dark:bg-fume-800 relative h-6 flex-1 overflow-hidden rounded">
                            <div className="bg-marka-500 h-full" style={{ width: `${w}%` }} />
                            <span className="text-fume-800 dark:text-fume-100 absolute right-2 top-1/2 -translate-y-1/2 font-mono text-xs font-semibold">
                              {Number(g.tutar).toLocaleString('tr-TR', {
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>
                          <span className="text-fume-400 w-10 shrink-0 text-right font-mono text-xs">
                            {g.sayi}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </KartIcerik>
            </Kart>

            <Kart>
              <KartBaslik>
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <CreditCard size={16} className="text-marka-500" /> Sağlayıcı Dağılımı
                </h3>
              </KartBaslik>
              <KartIcerik>
                {rapor.data.saglayiciDagilimi.length === 0 ? (
                  <p className="text-fume-500 py-6 text-center text-sm">Ödeme yok.</p>
                ) : (
                  <ul className="divide-fume-100 dark:divide-fume-800 divide-y">
                    {rapor.data.saglayiciDagilimi.map((s, i) => (
                      <li
                        key={`${s.saglayici}-${s.paraBirimi}-${i}`}
                        className="flex items-center justify-between py-2"
                      >
                        <div>
                          <span className="text-sm font-medium">{s.saglayici}</span>
                          <span className="text-fume-400 ml-2 text-xs">{s.paraBirimi}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-sm font-semibold">
                            {PARA_SEMBOL[s.paraBirimi]}
                            {Number(s.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </div>
                          <div className="text-fume-400 text-xs">{s.sayi} adet</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </KartIcerik>
            </Kart>
          </div>
        </>
      )}

      {rapor.isFetching && !rapor.isLoading && (
        <p className="text-fume-400 text-xs">
          <RefreshCcw size={11} className="inline animate-spin" /> Yenileniyor...
        </p>
      )}
    </div>
  );
}
